import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Heart, MessageCircle, ImagePlus, Send } from "lucide-react";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
}

function CommentSection({ postId }) {
    const { user } = useAuth();
    const [comments, setComments] = useState([]);
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/posts/${postId}/comments`);
                if (!cancelled) setComments(data);
            } catch (_e) {
                /* ignore */
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [postId]);

    const submit = async (e) => {
        e.preventDefault();
        if (!content.trim()) return;
        try {
            const { data } = await api.post(`/posts/${postId}/comments`, { content });
            setComments((prev) => [...prev, data]);
            setContent("");
        } catch (_e) {
            /* ignore */
        }
    };

    return (
        <div className="mt-4 border-t border-zinc-800 pt-4">
            {loading ? (
                <p className="text-xs text-zinc-600 font-body">Loading comments…</p>
            ) : (
                <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                    {comments.length === 0 && (
                        <p className="text-xs text-zinc-600 font-body">No comments yet — break the silence.</p>
                    )}
                    {comments.map((c) => (
                        <div key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
                            <div className="w-7 h-7 rounded-sm bg-zinc-800 flex items-center justify-center text-xs font-display font-bold text-zinc-400 shrink-0">
                                {c.author?.username?.[0]?.toUpperCase() || "?"}
                            </div>
                            <div className="flex-1 bg-zinc-900/50 rounded-sm px-3 py-2 border border-zinc-800/50">
                                <p className="text-xs text-zinc-500 font-body">
                                    <span className="text-zinc-300 font-semibold">@{c.author?.username || "user"}</span>{" "}
                                    · {timeAgo(c.created_at)}
                                </p>
                                <p className="text-sm text-zinc-200 font-body mt-0.5">{c.content}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {user && (
                <form onSubmit={submit} className="flex gap-2 mt-3" data-testid={`comment-form-${postId}`}>
                    <input
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Add a comment…"
                        className="flex-1 bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white placeholder:text-zinc-600 outline-none"
                        data-testid={`comment-input-${postId}`}
                    />
                    <button
                        type="submit"
                        className="bg-red-600 hover:bg-red-700 text-white px-3 rounded-sm transition-colors"
                        data-testid={`comment-submit-${postId}`}
                        aria-label="Submit comment"
                    >
                        <Send size={14} />
                    </button>
                </form>
            )}
        </div>
    );
}

function PostCard({ post, onLikeChange }) {
    const { user } = useAuth();
    const [showComments, setShowComments] = useState(false);
    const [liking, setLiking] = useState(false);

    const toggleLike = async () => {
        if (!user || liking) return;
        setLiking(true);
        try {
            const { data } = await api.post(`/posts/${post.id}/like`);
            onLikeChange(post.id, data);
        } catch (_e) {
            /* ignore */
        } finally {
            setLiking(false);
        }
    };

    return (
        <article
            className="bg-zinc-900/50 border border-zinc-800/60 rounded-sm overflow-hidden hover:border-zinc-700/60 transition-all duration-300"
            data-testid={`post-${post.id}`}
        >
            <header className="flex items-center gap-3 px-5 pt-5">
                <Link
                    to={`/u/${post.author?.username}`}
                    className="w-10 h-10 rounded-sm bg-zinc-800 flex items-center justify-center font-display font-black text-zinc-300"
                >
                    {post.author?.username?.[0]?.toUpperCase() || "?"}
                </Link>
                <div>
                    <Link
                        to={`/u/${post.author?.username}`}
                        className="font-body text-sm text-zinc-200 hover:text-white font-semibold"
                    >
                        {post.author?.name || "Unknown"}
                    </Link>
                    <p className="text-xs text-zinc-500 font-body">
                        @{post.author?.username || "anon"} · {timeAgo(post.created_at)}
                    </p>
                </div>
            </header>
            <div className="px-5 pt-3 pb-4">
                <p className="font-body text-zinc-200 whitespace-pre-wrap leading-relaxed">
                    {post.content}
                </p>
            </div>
            {post.media_url && (
                <div className="border-t border-zinc-800/60">
                    <img src={post.media_url} alt="post media" className="w-full max-h-[520px] object-cover" />
                </div>
            )}
            <footer className="px-5 py-4 border-t border-zinc-800/60 flex items-center gap-4 text-sm">
                <button
                    onClick={toggleLike}
                    disabled={!user}
                    className={`inline-flex items-center gap-1.5 transition-colors ${
                        post.liked_by_me ? "text-red-500" : "text-zinc-400 hover:text-red-500"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid={`post-like-btn-${post.id}`}
                >
                    <Heart
                        size={16}
                        fill={post.liked_by_me ? "currentColor" : "none"}
                        strokeWidth={post.liked_by_me ? 0 : 2}
                    />
                    <span className="font-body">{post.likes_count}</span>
                </button>
                <button
                    onClick={() => setShowComments((v) => !v)}
                    className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors"
                    data-testid={`post-comment-toggle-${post.id}`}
                >
                    <MessageCircle size={16} />
                    <span className="font-body">{post.comments_count}</span>
                </button>
            </footer>
            {showComments && (
                <div className="px-5 pb-5">
                    <CommentSection postId={post.id} />
                </div>
            )}
        </article>
    );
}

function ComposeBox({ onCreated }) {
    const { user } = useAuth();
    const [content, setContent] = useState("");
    const [media, setMedia] = useState("");
    const [showMedia, setShowMedia] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    if (!user) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!content.trim()) return;
        setSubmitting(true);
        setError("");
        try {
            const { data } = await api.post("/posts", { content, media_url: media || null });
            onCreated(data);
            setContent("");
            setMedia("");
            setShowMedia(false);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4"
            data-testid="compose-post-form"
        >
            <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={`What's the move, @${user.username}?`}
                rows={3}
                className="w-full bg-transparent text-white font-body placeholder:text-zinc-600 outline-none resize-none"
                data-testid="compose-content-input"
            />
            {showMedia && (
                <input
                    type="url"
                    value={media}
                    onChange={(e) => setMedia(e.target.value)}
                    placeholder="Image URL (https://…)"
                    className="w-full mt-2 bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white placeholder:text-zinc-600 outline-none"
                    data-testid="compose-media-input"
                />
            )}
            {error && <p className="text-xs text-red-500 font-body mt-2">{error}</p>}
            <div className="flex items-center justify-between mt-3">
                <button
                    type="button"
                    onClick={() => setShowMedia((v) => !v)}
                    className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white font-body"
                    data-testid="compose-toggle-media"
                >
                    <ImagePlus size={14} /> {showMedia ? "Hide image" : "Attach image URL"}
                </button>
                <button
                    type="submit"
                    disabled={submitting || !content.trim()}
                    className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-sm font-semibold tracking-wide text-sm transition-colors"
                    data-testid="compose-submit-btn"
                >
                    {submitting ? "Posting…" : "Post"}
                </button>
            </div>
        </form>
    );
}

export default function Feed() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/posts");
            setPosts(data);
        } catch (_e) {
            /* ignore */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleCreated = (post) => {
        setPosts((prev) => [post, ...prev]);
    };

    const handleLikeChange = (id, { liked, likes_count }) => {
        setPosts((prev) =>
            prev.map((p) =>
                p.id === id ? { ...p, liked_by_me: liked, likes_count } : p
            )
        );
    };

    return (
        <div className="min-h-screen bg-[var(--crewz-bg)]" data-testid="feed-page">
            <Navbar />
            <main className="max-w-3xl mx-auto px-4 py-10">
                <div className="mb-6">
                    <p className="overline">Live from the streets</p>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-white mt-2">
                        The Feed
                    </h1>
                </div>

                {user ? (
                    <div className="mb-6">
                        <ComposeBox onCreated={handleCreated} />
                    </div>
                ) : (
                    <div className="mb-6 bg-zinc-900/40 border border-zinc-800 rounded-sm p-5 text-sm text-zinc-400 font-body">
                        <Link to="/login" className="text-red-500 hover:text-red-400">
                            Sign in
                        </Link>{" "}
                        to post, like and comment.
                    </div>
                )}

                {loading ? (
                    <p className="text-zinc-500 font-body" data-testid="feed-loading">
                        Loading feed…
                    </p>
                ) : posts.length === 0 ? (
                    <p className="text-zinc-500 font-body" data-testid="feed-empty">
                        No posts yet. Be the first to start something.
                    </p>
                ) : (
                    <div className="space-y-6">
                        {posts.map((p) => (
                            <PostCard key={p.id} post={p} onLikeChange={handleLikeChange} />
                        ))}
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}
